import React, { useState, useEffect, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import projectLogo from "../beast-logo.png";
import downloadIcon from "../download.png";
import { serviceApi } from "../service";
import InfoTable from "../components/InfoTable.jsx";
import X3DAtoms from "../components/X3DAtoms.jsx";
import X3DModel from "../components/X3DModel.jsx";
import { DataPlot } from "../components/Plots.jsx";
import BeastNavbar from "../components/BeastNavbar.jsx";
import { AtomStructure, Atom } from "../AtomicStructure";
import { getPath } from "../utils";
import { adsorbatesToMechanisms } from "../ReactionPaths.js";
import { CatalystPathPlot } from "../components/CatalystPathPlot.jsx";

import BindingEnergyDataset from "../Datasets.js";

import DropdownButton from "react-bootstrap/DropdownButton";
import Dropdown from "react-bootstrap/Dropdown";
import Stack from "react-bootstrap/Stack";
import Figure from "react-bootstrap/Figure";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Navbar from "react-bootstrap/Navbar";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";

import "../x3dom.css";
import "../main.scss";

// A templated page generated for an adsorbed state
const AdsorbedStatePage = () => {
  const [data, setData] = useState([]);
  const [relatedData, setRelatedData] = useState([]);
  const [reactionData, setReactionData] = useState([]);
  const [columnUnits, setColumnUnits] = useState(null);
  const [extraOverviewRows, setExtraOverviewRows] = useState([]);

  let params = useParams();
  if (params["*"].split("/")[0] === "adsorbed_states") {
    var viewType = "adsorbed_state";
  } else if (params["*"].split("/")[0] === "catalysts") {
    var viewType = "catalyst";
  }

  useLayoutEffect(() => {
    document.body.style.backgroundColor = "#F3F3F3";
  });

  // Fetch units asynchronously on page load
  useEffect(() => {
    serviceApi
      .columns()
      .then((data) => {
        setColumnUnits(data.units);
      })
      .catch((error) => {
        console.error("Error fetching units from API:", error);
      });
  }, []);

  // Fetch data asynchronously on page load
  useEffect(() => {
    serviceApi
      .unfilteredQuery(
        {
          query: viewType,
          unique_id: params.adsorbedStateId,
        },
        "system_name",
      )
      .then((data) => {
        setData(data.result[0]);
        var relatedDataQueryObj = {
          query: viewType,
          [`${viewType}.catalyst_formula`]:
            data.result[0][viewType]["catalyst_formula"],
          [`${viewType}.catalyst_facet`]:
            data.result[0][viewType]["catalyst_facet"],
        };
        if (viewType === "adsorbed_state") {
          relatedDataQueryObj[`${viewType}.adsorbate_formula`] =
            data.result[0][viewType]["adsorbate_formula"];
        }
        serviceApi
          .unfilteredQuery(relatedDataQueryObj, "system_name")
          .then((relatedData) => {
            setRelatedData(relatedData.result);
          })
          .catch((error) => {
            console.error("Error fetching data from API:", error);
          });
        let reactionQueryObj = {
          query: "reaction",
          "reaction.catalyst_formula":
            data.result[0][viewType]["catalyst_formula"],
          "reaction.catalyst_facet": data.result[0][viewType]["catalyst_facet"],
        };
        if (viewType == "adsorbed_state") {
          reactionQueryObj["reaction.reaction_type"] =
            adsorbatesToMechanisms[data.result[0][viewType].adsorbate_formula];
        }
        serviceApi
          .unfilteredQuery(reactionQueryObj, "system_name")
          .then((reactionResult) => {
            let statePotential =
              data.result[0][viewType]["dft_solvation_expparams"][
                "applied_potential"
              ];
            let relatedReactions = [];
            reactionResult.result.forEach((reaction) => {
              if (reaction.reaction.potential == statePotential) {
                relatedReactions.push(reaction);
              }
            });
            if (relatedReactions.length > 0) {
              let relatedReactionComponent = "";
              relatedReactions.forEach((reaction) => {
                let reactionType = reaction.reaction.reaction_type;
                let reactionId = reaction.unique_id;
                let linkRef = `/catalyst_path/${reactionId}`;
                relatedReactionComponent = (
                  <div>
                    {relatedReactionComponent}
                    <a href={linkRef}>{reactionType}</a>
                  </div>
                );
              });
              let relatedReactionsLabel =
                relatedReactions.length > 1
                  ? "Related Reactions"
                  : "Related Reaction";
              setExtraOverviewRows([
                [relatedReactionsLabel, relatedReactionComponent],
              ]);
            }
          })
          .catch((error) => {
            console.error("Error fetching data from API:", error);
          });
      })
      .catch((error) => {
        console.error("Error fetching data from API:", error);
      });
  }, [params]);

  // Each table to be displayed (and the corresponding path within data)
  var root_table = [
    [viewType, "catalyst_elements"],
    [viewType, "catalyst_facet"],
    [viewType, "adsorbate_formula"],
    [viewType, "number_of_adsorbates"],
    [viewType, "dft_solvation_expparams", "applied_potential"],
  ];

  var dft_settings_table = [
    [viewType, "dft_settings", "xc_functional"],
    [viewType, "dft_settings", "vdw_correction"],
    [viewType, "dft_settings", "pseudopotential_class"],
    [viewType, "dft_solvation_settings", "implicit_solvation_model"],
    [viewType, "dft_solvation_settings", "pcm_variant"],
  ];

  var coulomb_truncation_info = [
    [viewType, "dft_settings", "coulomb_truncation_ion_margin"],
    [viewType, "dft_settings", "coulomb_truncation_direction"],
    [viewType, "dft_settings", "coulomb_truncation_Rc"],
    [viewType, "dft_settings", "coulomb_truncation_embed"],
    [viewType, "dft_settings", "coulomb_truncation_type"],
  ];

  var dft_results_table = [
    [viewType, "dft_results", "adsorption_energy"],
    [viewType, "dft_results", "net_charge_state"],
    [viewType, "dft_results", "net_spin_moment"],
  ];

  // Trigger the browser to save a file with the given data
  var saveData = (function () {
    var a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    return function (data, fileName) {
      var blob = new Blob([data], { type: "octet/stream" }),
        url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    };
  })();

  // Function to trigger the browser to save the state's structure file
  function saveStructure() {
    let structureData = getPath(
      [viewType, "dft_results", "dft_physical_structure"],
      data,
    );
    if (structureData != null) {
      saveData(
        structureData.join(""),
        "BEAST_DB_" + params.adsorbedStateId + ".vasp",
      );
    } else {
      alert("Could not obtain structure data for this system.");
    }
  }

  // Function to trigger the browser to save the state's JDFTx input
  function saveJDFTx() {
    let structureData = getPath(
      [viewType, "dft_results", "dft_physical_structure"],
      data,
    );
    if (structureData != null) {
      var atomsView = new AtomStructure(structureData, 1.0);
      let angToBohr = 1.8897259886;

      let latticeSpec = `lattice ${angToBohr * atomsView.R[0][0]} ${angToBohr * atomsView.R[0][1]} ${angToBohr * atomsView.R[0][2]} \\
${angToBohr * atomsView.R[1][0]} ${angToBohr * atomsView.R[1][1]} ${angToBohr * atomsView.R[1][2]} \\
${angToBohr * atomsView.R[2][0]} ${angToBohr * atomsView.R[2][1]} ${angToBohr * atomsView.R[2][2]}`;

      let ions = "";
      let ionCount = 0;
      atomsView.species.map((specie, specie_i) => {
        let type = specie[0];
        let amount = specie[1];
        for (let i = 0; i < amount; i++) {
          let atom = atomsView.atoms[ionCount];
          ions += `ion ${type} ${angToBohr*atom.pos[0]} ${angToBohr*atom.pos[1]} ${angToBohr*atom.pos[2]} ${atomsView.moveScale[ionCount]}\n`;
          ionCount++;
        }
      });

      let k_x = getPath([viewType, "dft_settings", "kgrid", 0], data);
      let k_y = getPath([viewType, "dft_settings", "kgrid", 1], data);
      let k_z = getPath([viewType, "dft_settings", "kgrid", 2], data);
      let planewave_cutoff = getPath(
        [viewType, "dft_settings", "planewave_cutoff"],
        data,
      );
      let n_bands = getPath(
        [viewType, "dft_results", "dft_electronic_structure", "Nbands"],
        data,
      );
      let smearing_width = getPath(
        [viewType, "dft_settings", "smearing_width"],
        data,
      );
      let smearing_type = getPath(
        [viewType, "dft_settings", "smearing_type"],
        data,
      );
      let fermi_level = getPath([viewType, "dft_results", "fermi_level"], data);
      let embed_x = getPath(
        [viewType, "dft_settings", "coulomb_truncation_embed", 0],
        data,
      );
      let embed_y = getPath(
        [viewType, "dft_settings", "coulomb_truncation_embed", 1],
        data,
      );
      let embed_z = getPath(
        [viewType, "dft_settings", "coulomb_truncation_embed", 2],
        data,
      );

      let jdftx_input = `${latticeSpec}

dump-name $VAR
initial-state $VAR
kpoint-folding ${k_x} ${k_y} ${k_z}
elec-cutoff ${planewave_cutoff} 100
elec-ex-corr gga
van-der-waals D3
elec-n-bands ${n_bands}
electronic-minimize nIterations 100 energyDiffThreshold 1e-07
elec-smearing ${smearing_type} ${smearing_width}
elec-initial-magnetization 0 no
spintype z-spin
core-overlap-check none
converge-empty-states yes
latt-move-scale 0 0 0
lattice-minimize nIterations 00
symmetries none
fluid LinearPCM
pcm-variant CANDLE
fluid-solvent H2O
fluid-cation Na+ 0.5
fluid-anion F- 0.5
dump End Dtot BoundCharge
dump End State
dump End Forces
dump End Ecomponents
dump End VfluidTot
dump End ElecDensity
dump End KEdensity
dump End EigStats
dump End BandEigs
dump End DOS
target-mu ${fermi_level}

coords-type cartesian
${ions}
ion-species GBRV/$ID_pbe.uspp

coulomb-interaction slab 001
coulomb-truncation-embed ${embed_x} ${embed_y} ${embed_z}
dump End Forces
dump End Ecomponents`;

      saveData(jdftx_input, "totalE_BEAST_" + params.adsorbedStateId + ".in");
    } else {
      alert("Could not obtain structure data for this system.");
    }
  }

  // Get structure data for 3D view and potential plots

  var structureData = getPath(
    [viewType, "dft_results", "dft_physical_structure"],
    data,
  );

  // Get related structures to pass to potential plots

  if (relatedData && structureData) {
    var thisStructure = new AtomStructure(structureData);
    var relatedStructureData = relatedData.map((system, i) => {
      return new AtomStructure(
        getPath([viewType, "dft_results", "dft_physical_structure"], system),
      );
    });
    var structureDistances = relatedStructureData.map((structure, i) => {
      return thisStructure.similarity(structure);
    });
  }

  // Only needed if X3D explicitly embedded in data (may happen later...)
  //
  //var x3dData = getPath([viewType, "x3d_view"], data);
  //if (data !== undefined) {
  //  if (data.length !== 0) {
  //    var pageTitle = data[viewType]["system_name"];
  //  } else {
  //    var pageTitle = "Loading...";
  //  }
  //} else {
  //  var pageTitle = "Loading...";
  //}

  var bindingEnergyPlotId = "potentialPlot";
  var bindingEnergyXAxisTitle = "Applied Potential (V)";
  var bindingEnergyYAxisTitle = "Adsorption Energy (eV)";

  // Set a slightly higher view distance for catalysts
  // (typically the adsorbate brings the camera a little further)
  let viewDistanceMult = viewType === "adsorbed_state" ? 1.4 : 1.5;

  return (
    <div className="App">
      <BeastNavbar selected="adsorbate" />
      <div className="wrapper">
        <Container className="pt-3" fluid="md">
          <Row gap={3}>
            <Stack direction="vertical" gap={3} className="mb-3 col-md-4">
              <Card className="shadow-sm">
                <Card.Title className="pt-3">Overview</Card.Title>
                <Container className="pt-3">
                  <InfoTable
                    data={data}
                    rows={root_table}
                    units={columnUnits}
                    extraRows={extraOverviewRows}
                  />
                </Container>
              </Card>
              <Card className="shadow-sm">
                <Card.Title className="pt-3">DFT Results</Card.Title>
                <Container className="pt-3">
                  <InfoTable
                    data={data}
                    rows={dft_results_table}
                    units={columnUnits}
                  />
                </Container>
              </Card>
              <Card className="shadow-sm">
                <Card.Title className="pt-3">DFT Settings</Card.Title>
                <Container className="pt-3">
                  <InfoTable
                    data={data}
                    rows={dft_settings_table}
                    units={columnUnits}
                  />
                </Container>
              </Card>
            </Stack>
            <Stack direction="vertical" gap={3} className="mb-3 col-md-4">
              <Card className="shadow-sm">
                <Stack>
                  <Row>
                    <Col></Col>
                    <Col xs={6} sm={6} lg={6}>
                      <Card.Title className="mx-4 my-4">
                        DFT Structure
                      </Card.Title>
                    </Col>
                    <Col
                      xs={4}
                      sm={3}
                      md={4}
                      lg={3}
                      className="px-4 py-4"
                      style={{ display: "flex", justifyContent: "right" }}
                    >
                      <DropdownButton
                        title="Download"
                        style={{ display: "flex", justifyContent: "right" }}
                      >
                        <Dropdown.Item onClick={saveJDFTx} key={0}>
                          JDFTx Input
                        </Dropdown.Item>
                        <Dropdown.Item onClick={saveStructure} key={1}>
                          Structure
                        </Dropdown.Item>
                      </DropdownButton>
                    </Col>
                  </Row>
                </Stack>
                {structureData ? (
                  <X3DAtoms
                    structureData={structureData}
                    viewDistanceMult={viewDistanceMult}
                  />
                ) : (
                  <div style={{ position: "relative", height: "400px" }}>
                    <div
                      style={{ position: "absolute", top: "45%" }}
                      className="spinner-border text-info"
                      role="status"
                    >
                      {" "}
                      <span className="sr-only"></span>{" "}
                    </div>
                  </div>
                )}
              </Card>
              <Card className="shadow-sm">
                {data && relatedData?.length !== 0 ? (
                  <DataPlot
                    xAxisTitle={"Applied Potential (V)"}
                    yAxisTitle={"Adsorption Energy (eV)"}
                    plotId={"bindingEnergyPlot"}
                    viewType={viewType}
                    currentData={data}
                    relatedData={relatedData}
                  />
                ) : (
                  <div style={{ position: "relative", height: "400px" }}>
                    <div
                      style={{ position: "absolute", top: "45%" }}
                      className="spinner-border text-info"
                      role="status"
                    >
                      {" "}
                      <span className="sr-only"></span>{" "}
                    </div>
                  </div>
                )}
              </Card>
            </Stack>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default AdsorbedStatePage;
